import { useEffect, useMemo, useState, FormEvent } from 'react';
import { client } from '../../sdkAdapter';
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../ui/table";
import Badge from "../ui/badge/Badge";
import Pagination from "../ui/pagination";
import CatalogModalFrame from './CatalogModalFrame';
import {
    confirmAction,
    showErrorAlert,
    showSuccessToast,
    showValidationAlert,
} from './catalogAlerts';

type AttributeType = 'text' | 'textarea' | 'price' | 'boolean' | 'select' | 'date';

interface AttributeRecord {
    id: number;
    name: string;
    code: string;
    type?: AttributeType;
    is_filterable?: boolean | number | null;
    is_searchable?: boolean | number | null;
    active?: boolean | number | null;
    created_at?: string;
    updated_at?: string;
}

interface AttributeValueRecord {
    id: number;
    name: string;
    attribute_id: number;
}

interface AttributeFormState {
    name: string;
    code: string;
    type: AttributeType;
    is_filterable: boolean;
    is_searchable: boolean;
    active: boolean;
}

interface ValidatedAttributeForm {
    name: string;
    code: string;
    type: AttributeType;
    is_filterable: boolean;
    is_searchable: boolean;
    active: boolean;
}

const ATTRIBUTE_TYPES: AttributeType[] = ['text', 'textarea', 'price', 'boolean', 'select', 'date'];

const emptyForm = (): AttributeFormState => ({
    name: '',
    code: '',
    type: 'text',
    is_filterable: true,
    is_searchable: true,
    active: true,
});

const toBoolean = (value: unknown): boolean => value === true || value === 1 || value === '1';

const extractList = (response: any) => {
    const payload = response?.data?.data ?? response?.data ?? response;

    if (Array.isArray(payload)) {
        return payload;
    }

    if (Array.isArray(payload?.data)) {
        return payload.data;
    }

    return [];
};

const getApiErrorMessage = (err: any, fallback: string): string => {
    const message = err?.response?.data?.messages || err?.response?.data?.message || err?.message;

    if (Array.isArray(message)) {
        return message.join(', ');
    }

    return typeof message === 'string' && message.trim() ? message : fallback;
};

const validateAttributeForm = (form: AttributeFormState): { valid: true; data: ValidatedAttributeForm; } | { valid: false; message: string; } => {
    const name = form.name.trim();
    const code = form.code.trim();

    if (!name || !code) {
        return { valid: false, message: 'Name and code are required.' };
    }

    if (name.length > 50) {
        return { valid: false, message: 'Attribute name must be 50 characters or fewer.' };
    }

    if (code.length > 50) {
        return { valid: false, message: 'Attribute code must be 50 characters or fewer.' };
    }

    if (!ATTRIBUTE_TYPES.includes(form.type)) {
        return { valid: false, message: 'Attribute type is invalid.' };
    }

    return {
        valid: true,
        data: {
            name,
            code,
            type: form.type,
            is_filterable: form.is_filterable,
            is_searchable: form.is_searchable,
            active: form.active,
        }
    };
};

const validateAttributeValueName = (value: string): { valid: true; name: string; } | { valid: false; message: string; } => {
    const name = value.trim();

    if (!name) {
        return { valid: false, message: 'Option name is required.' };
    }

    if (name.length > 50) {
        return { valid: false, message: 'Option name must be 50 characters or fewer.' };
    }

    return { valid: true, name };
};

export default function CatalogAttributes() {
    const [allAttributes, setAllAttributes] = useState<AttributeRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [form, setForm] = useState<AttributeFormState>(emptyForm);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [isOptionsModalOpen, setIsOptionsModalOpen] = useState(false);
    const [currentAttributeId, setCurrentAttributeId] = useState<number | null>(null);
    const [attributeValues, setAttributeValues] = useState<AttributeValueRecord[]>([]);
    const [newValueName, setNewValueName] = useState('');
    const [editingValueId, setEditingValueId] = useState<number | null>(null);
    const [editingValueName, setEditingValueName] = useState('');
    const [valuesLoading, setValuesLoading] = useState(false);

    const totalItems = allAttributes.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage));

    const attributes = useMemo(() => {
        const startIndex = (currentPage - 1) * rowsPerPage;
        return allAttributes.slice(startIndex, startIndex + rowsPerPage);
    }, [allAttributes, currentPage, rowsPerPage]);

    useEffect(() => {
        refreshAttributes();
    }, []);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const refreshAttributes = async (options?: { focusAttributeId?: number; targetPage?: number; }) => {
        setLoading(true);
        try {
            const res = await client.getApiCatalogAttributes() as any;
            const data = extractList(res) as AttributeRecord[];
            setAllAttributes(Array.isArray(data) ? data : []);

            if (options?.focusAttributeId) {
                const focusedIndex = data.findIndex((attribute) => attribute.id === options.focusAttributeId);
                if (focusedIndex >= 0) {
                    setCurrentPage(Math.floor(focusedIndex / rowsPerPage) + 1);
                    return;
                }
            }

            if (options?.targetPage) {
                const nextTotalPages = Math.max(1, Math.ceil(data.length / rowsPerPage));
                setCurrentPage(Math.min(options.targetPage, nextTotalPages));
            }
        } catch (err) {
            console.error('Failed to fetch attributes:', err);
            setAllAttributes([]);
            await showErrorAlert(getApiErrorMessage(err, 'Failed to load attributes'));
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (attribute: AttributeRecord) => {
        setEditingId(attribute.id);
        setForm({
            name: attribute.name || '',
            code: attribute.code || '',
            type: attribute.type || 'text',
            is_filterable: toBoolean(attribute.is_filterable ?? true),
            is_searchable: toBoolean(attribute.is_searchable ?? true),
            active: toBoolean(attribute.active ?? true)
        });
        setIsModalOpen(true);
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setForm(emptyForm());
        setIsModalOpen(false);
    };

    const createAttribute = async () => {
        const validated = validateAttributeForm(form);
        if (!validated.valid) {
            await showValidationAlert(validated.message);
            return;
        }

        setSubmitting(true);
        try {
            const response = await client.postApiCatalogAttributeCreate({
                postApiCatalogAttributeCreateRequest: validated.data as any
            }) as any;

            const createdAttributeId = Number(response?.data?.data?.id ?? response?.data?.id);

            handleCancelEdit();
            await refreshAttributes(Number.isFinite(createdAttributeId) ? { focusAttributeId: createdAttributeId } : undefined);
            showSuccessToast('Attribute created successfully');
        } catch (err: any) {
            await showErrorAlert(getApiErrorMessage(err, 'Failed to create attribute'));
        } finally {
            setSubmitting(false);
        }
    };

    const updateAttribute = async () => {
        const validated = validateAttributeForm(form);
        if (!validated.valid || editingId === null) {
            await showValidationAlert(validated.valid ? 'Attribute ID is missing.' : validated.message);
            return;
        }

        setSubmitting(true);
        try {
            await client.postApiCatalogAttributeUpdate({
                postApiCatalogAttributeUpdateRequest: {
                    id: editingId,
                    ...validated.data,
                } as any
            });

            handleCancelEdit();
            await refreshAttributes({ focusAttributeId: editingId });
            showSuccessToast('Attribute updated successfully');
        } catch (err: any) {
            await showErrorAlert(getApiErrorMessage(err, 'Failed to update attribute'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        if (editingId !== null) {
            await updateAttribute();
            return;
        }

        await createAttribute();
    };

    const openOptionsModal = async (attrId: number) => {
        setCurrentAttributeId(attrId);
        setEditingValueId(null);
        setEditingValueName('');
        setNewValueName('');
        setIsOptionsModalOpen(true);
        await fetchAttributeValues(attrId);
    };

    const fetchAttributeValues = async (attrId: number) => {
        setValuesLoading(true);
        try {
            const res = await client.getApiCatalogAttributesValues() as any;
            const allValues = extractList(res) as AttributeValueRecord[];
            const filtered = Array.isArray(allValues) ? allValues.filter((value) => Number(value.attribute_id) === attrId) : [];
            setAttributeValues(filtered);
        } catch (err) {
            console.error('Failed to fetch values:', err);
            setAttributeValues([]);
        } finally {
            setValuesLoading(false);
        }
    };

    const createAttributeValue = async () => {
        if (!currentAttributeId) {
            await showValidationAlert('Attribute ID is missing.');
            return;
        }

        const validated = validateAttributeValueName(newValueName);
        if (!validated.valid) {
            await showValidationAlert(validated.message);
            return;
        }

        setValuesLoading(true);
        try {
            await client.postApiCatalogAttributeValueCreate({
                postApiCatalogAttributeValueCreateRequest: {
                    name: validated.name,
                    attribute_id: currentAttributeId
                }
            } as any);
            setNewValueName('');
            await fetchAttributeValues(currentAttributeId);
            showSuccessToast('Option created successfully');
        } catch (err: any) {
            await showErrorAlert(getApiErrorMessage(err, 'Failed to create option'));
        } finally {
            setValuesLoading(false);
        }
    };

    const startEditingValue = (value: AttributeValueRecord) => {
        setEditingValueId(value.id);
        setEditingValueName(value.name);
    };

    const cancelEditingValue = () => {
        setEditingValueId(null);
        setEditingValueName('');
    };

    const updateAttributeValue = async () => {
        if (!currentAttributeId || editingValueId === null) {
            await showValidationAlert('Option ID is missing.');
            return;
        }

        const validated = validateAttributeValueName(editingValueName);
        if (!validated.valid) {
            await showValidationAlert(validated.message);
            return;
        }

        setValuesLoading(true);
        try {
            await client.postApiCatalogAttributeValueUpdate({
                postApiCatalogAttributeValueUpdateRequest: {
                    id: editingValueId,
                    name: validated.name,
                    attribute_id: currentAttributeId,
                }
            } as any);

            cancelEditingValue();
            await fetchAttributeValues(currentAttributeId);
            showSuccessToast('Option updated successfully');
        } catch (err: any) {
            await showErrorAlert(getApiErrorMessage(err, 'Failed to update option'));
        } finally {
            setValuesLoading(false);
        }
    };

    const deleteAttributeValue = async (id: number) => {
        const isConfirmed = await confirmAction({
            text: 'This option will be permanently deleted.',
            confirmButtonText: 'Delete Option'
        });

        if (!isConfirmed) {
            return;
        }

        try {
            await client.postApiCatalogAttributeValueDelete({
                postApiCatalogAttributeValueDeleteRequest: { id }
            } as any);

            if (currentAttributeId) {
                await fetchAttributeValues(currentAttributeId);
            }

            if (editingValueId === id) {
                cancelEditingValue();
            }

            showSuccessToast('Option deleted successfully');
        } catch (err: any) {
            await showErrorAlert(getApiErrorMessage(err, 'Failed to delete option'));
        }
    };

    const handleDelete = async (attributeId: number) => {
        const isConfirmed = await confirmAction({
            text: 'This attribute will be permanently deleted.',
            confirmButtonText: 'Delete Attribute'
        });

        if (!isConfirmed) {
            return;
        }

        try {
            await client.postApiCatalogAttributeDelete({
                postApiCatalogAttributeDeleteRequest: { id: attributeId }
            });

            if (editingId === attributeId) {
                handleCancelEdit();
            }

            const nextPage = attributes.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
            await refreshAttributes({ targetPage: nextPage });
            showSuccessToast('Attribute deleted successfully');
        } catch (err: any) {
            console.error(err);
            await showErrorAlert(getApiErrorMessage(err, 'Failed to delete attribute'));
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Attributes</h2>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Manage attribute metadata, storefront behavior, and select-type options from the admin panel.
                    </p>
                </div>
                <button
                    onClick={() => {
                        handleCancelEdit();
                        setIsModalOpen(true);
                    }}
                    className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition-all hover:bg-brand-600 active:scale-95"
                >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Create Attribute
                </button>
            </div>

            <CatalogModalFrame
                isOpen={isModalOpen}
                onClose={handleCancelEdit}
                title={editingId !== null ? 'Edit Attribute' : 'Create Attribute'}
                className="max-w-2xl"
                formId="catalog-attribute-form"
                submitLabel={submitting ? 'Processing...' : editingId !== null ? 'Update Attribute' : 'Create Attribute'}
                submitDisabled={submitting}
            >
                <form id="catalog-attribute-form" onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div>
                            <label className="mb-2 block text-sm font-bold text-gray-700 dark:text-gray-300">
                                Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-800 outline-none transition-all focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                                placeholder="e.g. Color"
                                maxLength={50}
                                required
                            />
                        </div>
                        <div>
                            <label className="mb-2 block text-sm font-bold text-gray-700 dark:text-gray-300">
                                Code <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={form.code}
                                onChange={(e) => setForm({ ...form, code: e.target.value })}
                                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-800 outline-none transition-all focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                                placeholder="e.g. color"
                                maxLength={50}
                                required
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div>
                            <label className="mb-2 block text-sm font-bold text-gray-700 dark:text-gray-300">Type</label>
                            <select
                                value={form.type}
                                onChange={(e) => setForm({ ...form, type: e.target.value as AttributeType })}
                                className="w-full cursor-pointer appearance-none rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-800 outline-none transition-all focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                            >
                                <option value="text">Text</option>
                                <option value="textarea">Textarea</option>
                                <option value="price">Price</option>
                                <option value="boolean">Boolean</option>
                                <option value="select">Select</option>
                                <option value="date">Date</option>
                            </select>
                        </div>
                        <div>
                            <label className="mb-2 block text-sm font-bold text-gray-700 dark:text-gray-300">Status</label>
                            <select
                                value={form.active ? 'true' : 'false'}
                                onChange={(e) => setForm({ ...form, active: e.target.value === 'true' })}
                                className="w-full cursor-pointer appearance-none rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-800 outline-none transition-all focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                            >
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <label className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
                            <span className="text-sm font-bold text-gray-700 dark:text-gray-300">Filterable</span>
                            <input
                                type="checkbox"
                                checked={form.is_filterable}
                                onChange={(e) => setForm({ ...form, is_filterable: e.target.checked })}
                                className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                            />
                        </label>
                        <label className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
                            <span className="text-sm font-bold text-gray-700 dark:text-gray-300">Searchable</span>
                            <input
                                type="checkbox"
                                checked={form.is_searchable}
                                onChange={(e) => setForm({ ...form, is_searchable: e.target.checked })}
                                className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                            />
                        </label>
                    </div>
                </form>
            </CatalogModalFrame>

            <CatalogModalFrame
                isOpen={isOptionsModalOpen}
                onClose={() => {
                    setIsOptionsModalOpen(false);
                    cancelEditingValue();
                }}
                title="Manage Options"
                className="max-w-xl"
                footer={(
                    <div className="flex justify-end gap-3 border-t border-gray-100 bg-gray-50/50 px-6 py-5 dark:border-gray-800 dark:bg-gray-800/30">
                        <button
                            type="button"
                            onClick={() => {
                                setIsOptionsModalOpen(false);
                                cancelEditingValue();
                            }}
                            className="rounded-xl border border-gray-200 bg-white px-6 py-2.5 text-sm font-bold text-gray-700 transition-all hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                            Close
                        </button>
                    </div>
                )}
            >
                <div className="space-y-4">
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={newValueName}
                            onChange={(e) => setNewValueName(e.target.value)}
                            placeholder="New option name (e.g. Red)"
                            maxLength={50}
                            className="flex-1 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-800 outline-none transition-all focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    void createAttributeValue();
                                }
                            }}
                        />
                        <button
                            type="button"
                            onClick={createAttributeValue}
                            disabled={!newValueName.trim() || valuesLoading}
                            className="rounded-xl bg-brand-500 px-4 py-2.5 text-sm font-bold text-white hover:bg-brand-600 disabled:opacity-50"
                        >
                            Add
                        </button>
                    </div>

                    <div className="mt-4 max-h-72 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-800">
                        {valuesLoading && attributeValues.length === 0 ? (
                            <div className="p-4 text-center text-sm text-gray-500">Loading...</div>
                        ) : attributeValues.length === 0 ? (
                            <div className="p-4 text-center text-sm text-gray-500">No options found. Add one above.</div>
                        ) : (
                            <div className="divide-y divide-gray-100 dark:divide-gray-800">
                                {attributeValues.map((value) => (
                                    <div key={value.id} className="flex items-center justify-between gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                        {editingValueId === value.id ? (
                                            <input
                                                type="text"
                                                value={editingValueName}
                                                onChange={(e) => setEditingValueName(e.target.value)}
                                                maxLength={50}
                                                className="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 outline-none transition-all focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                                            />
                                        ) : (
                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{value.name}</span>
                                        )}

                                        <div className="flex items-center gap-2">
                                            {editingValueId === value.id ? (
                                                <>
                                                    <button
                                                        type="button"
                                                        onClick={updateAttributeValue}
                                                        className="rounded-lg border border-green-200 bg-green-50 px-3 py-1.5 text-xs font-bold text-green-700 transition-all hover:bg-green-100 dark:border-green-900/30 dark:bg-green-900/20 dark:text-green-400"
                                                    >
                                                        Save
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={cancelEditingValue}
                                                        className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-bold text-gray-600 transition-all hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                                                    >
                                                        Cancel
                                                    </button>
                                                </>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => startEditingValue(value)}
                                                    className="text-gray-400 transition-colors hover:text-brand-500"
                                                    title="Edit Option"
                                                >
                                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                    </svg>
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => deleteAttributeValue(value.id)}
                                                className="text-gray-400 transition-colors hover:text-red-500"
                                                title="Delete Option"
                                            >
                                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </CatalogModalFrame>

            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
                <div className="border-b border-gray-200 bg-gray-50/50 p-5 dark:border-gray-800 dark:bg-gray-800/50">
                    <h3 className="text-lg font-bold text-gray-800 dark:text-white">Existing Attributes</h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {loading ? 'Loading attributes...' : `${totalItems} attribute${totalItems === 1 ? '' : 's'} loaded.`}
                    </p>
                </div>
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableCell isHeader className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-gray-400">Name</TableCell>
                                <TableCell isHeader className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-gray-400">Code</TableCell>
                                <TableCell isHeader className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-gray-400">Type</TableCell>
                                <TableCell isHeader className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-400">Flags</TableCell>
                                <TableCell isHeader className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-400">Status</TableCell>
                                <TableCell isHeader className="px-6 py-4 text-end text-xs font-bold uppercase tracking-wider text-gray-400">Actions</TableCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="px-6 py-12 text-center font-medium text-gray-500 dark:text-gray-400">
                                        Loading attributes...
                                    </TableCell>
                                </TableRow>
                            ) : attributes.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="px-6 py-12 text-center font-medium text-gray-500 dark:text-gray-400">
                                        No attributes found.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                attributes.map((attribute) => (
                                    <TableRow key={attribute.id} className={`group transition-all hover:bg-gray-50 dark:hover:bg-gray-800/50 ${editingId === attribute.id ? 'bg-brand-50/50 dark:bg-brand-900/10' : ''}`}>
                                        <TableCell className="px-6 py-4 font-bold text-gray-700 dark:text-gray-200">
                                            {attribute.name || <span className="italic text-gray-400">No Name</span>}
                                        </TableCell>
                                        <TableCell className="px-6 py-4 font-mono text-xs text-gray-500">
                                            {attribute.code || <span className="text-gray-300">-</span>}
                                        </TableCell>
                                        <TableCell className="px-6 py-4 uppercase text-[10px] font-bold tracking-wider">
                                            <span className="rounded bg-gray-100 px-2 py-1 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                                                {attribute.type || 'text'}
                                            </span>
                                        </TableCell>
                                        <TableCell className="px-6 py-4 text-center">
                                            <div className="flex justify-center gap-2">
                                                <Badge color={toBoolean(attribute.is_filterable) ? 'info' : 'light'} size="sm">
                                                    {toBoolean(attribute.is_filterable) ? 'Filterable' : 'No Filter'}
                                                </Badge>
                                                <Badge color={toBoolean(attribute.is_searchable) ? 'primary' : 'light'} size="sm">
                                                    {toBoolean(attribute.is_searchable) ? 'Searchable' : 'No Search'}
                                                </Badge>
                                            </div>
                                        </TableCell>
                                        <TableCell className="px-6 py-4 text-center">
                                            <Badge color={toBoolean(attribute.active ?? true) ? 'success' : 'error'} size="sm">
                                                {toBoolean(attribute.active ?? true) ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="px-6 py-4 text-end">
                                            <div className="flex justify-end gap-3">
                                                {attribute.type === 'select' && (
                                                    <button
                                                        onClick={() => openOptionsModal(attribute.id)}
                                                        className="rounded-lg border border-gray-200 bg-white p-2 text-gray-500 shadow-sm transition-all hover:border-blue-200 hover:text-blue-500 active:scale-95 dark:border-gray-700 dark:bg-gray-800"
                                                        title="Manage Options"
                                                    >
                                                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                                                        </svg>
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => handleEdit(attribute)}
                                                    className="rounded-lg border border-gray-200 bg-white p-2 text-gray-500 shadow-sm transition-all hover:border-brand-200 hover:text-brand-500 active:scale-95 dark:border-gray-700 dark:bg-gray-800"
                                                    title="Edit Attribute"
                                                >
                                                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(attribute.id)}
                                                    className="rounded-lg border border-gray-200 bg-white p-2 text-gray-500 shadow-sm transition-all hover:border-red-200 hover:text-red-500 active:scale-95 dark:border-gray-700 dark:bg-gray-800"
                                                    title="Delete Attribute"
                                                >
                                                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>

                {!loading && totalItems > 0 ? (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(rows) => {
                            setRowsPerPage(rows);
                            setCurrentPage(1);
                        }}
                        totalItems={totalItems}
                    />
                ) : null}
            </div>
        </div>
    );
}
